import Button from '@/common/components/Button';
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { ShowHeader } from '@/common/components/ShowHeader';
import Tabs from '@/common/components/Tabs';
import { tabsDefaultWithFlexGrowClassName } from '@/common/constants/tabsDefaultClassName.constant';
import { useCurrentUser } from '@/common/hooks/useCurrentUser';
import useDebounce from '@/common/hooks/useDebounce';
import useUserPermissions from '@/common/hooks/useUserPermissions';
import { cn } from '@/common/utils/cn';
import { useMineCompany } from '@/features/company/hooks/useMineCompany';
import { SearchIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CreateDashboard from '../../components/Dashboard/CreateDashboard';
import Dashboard from '../../components/Dashboard/Dashboard';
import EmptyDashboards from '../../components/Dashboard/EmptyDashboards';
import { useDashboards } from '../../hooks/dashboards/useDashboards';
import { DashboardData } from '../../types/dashboards/dashboard.type';

const ROUTE_PREFIX = '/company/d';

const Show = () => {
  const {
    data: company,
  } = useMineCompany();

  const tabContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [currentTab, setCurrentTab] = useState<string>();

  const [initialWidth, setInitialWidth] = useState(1200);

  const [isSearchEnabled, setIsSearchEnabled] = useState(false);

  const [debouncedSearch, setSearch, search] = useDebounce('');

  const { data: currentUser } = useCurrentUser({ staleTime: Infinity });

  const currentUserId = currentUser?.id;

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasPermissionToUpdateDashboard = useMemo(() => {
    return isUserPermissionsPending || hasUserPermission('Client::Dashboards::Update');
  }, [isUserPermissionsPending]);

  const {
    data: dashboards,
    isPending: dashboardsIsPending,
    dataUpdatedAt: dashboardsUpdatedAt,
    error: dashboardsError = {} as any,
    refetch: refetchDashboards,
  } = useDashboards<DashboardData[]>({
    filters: {
      search: debouncedSearch,
    }
  });

  const filteredDashboards = useMemo(() => {
    if (!isSearchEnabled) {
      return dashboards;
    }

    const filteredDashboards = dashboards.filter((dashboard) => {
      return dashboard.display_name.toUpperCase().includes(debouncedSearch.toUpperCase());
    });

    const filteredDashboardsIds = filteredDashboards.map(({ id }) => id);

    if (filteredDashboards.length > 0 && (!currentTab || !filteredDashboardsIds.includes(currentTab))) {
      setCurrentTab(filteredDashboards[0].id);
    }

    return filteredDashboards;
  }, [dashboardsUpdatedAt, currentTab, isSearchEnabled, debouncedSearch]);

  const emptyDashboards = dashboards?.length === 0 && !dashboardsIsPending;

  const onCreateDashboard = useCallback((dashboardId: string) => {
    setCurrentTab(dashboardId);
    navigate(`${ROUTE_PREFIX}/${dashboardId}`);
  }, [navigate]);

  const onDeleteDashboard = useCallback((index: number) => {
    const previousDashboard = dashboards[index - 1] || dashboards[index + 1];

    if (!previousDashboard) {
      setCurrentTab(undefined);
      navigate(ROUTE_PREFIX);

      return;
    }

    setCurrentTab(previousDashboard.id);
    navigate(`${ROUTE_PREFIX}/${previousDashboard.id}`);
  }, [navigate, currentTab, dashboardsUpdatedAt]);

  const handleSetCurrentTab = useCallback((id: string | undefined) => {
    if (!id)
      return;

    if (id === currentTab) {
      return navigate(`${ROUTE_PREFIX}/${id}${location.search}`);
    }

    setCurrentTab(id);

    return navigate(`${ROUTE_PREFIX}/${id}`);
  }, [navigate, currentTab, location.search]);

  useEffect(() => {
    const el = tabContainerRef.current;

    if (!el)
      return;

    setInitialWidth(el.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      setInitialWidth(entry.contentRect.width);
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!dashboardsIsPending && dashboards?.[0]?.id) {
      const pathParts = location.pathname.split('/').filter(Boolean);
      const lastPart = pathParts[pathParts.length - 1];
      const tabFromUrl = dashboards.some(d => d.id === lastPart) ? lastPart : dashboards[0].id;

      setCurrentTab(tabFromUrl);
      navigate(`${ROUTE_PREFIX}/${tabFromUrl}${location.search}`, { replace: true });
    }
  }, [dashboardsIsPending]);

  useEffect(() => {
    if (isSearchEnabled) {
      searchInputRef.current?.focus();
    }
  }, [isSearchEnabled]);

  return (
    <div className="inner-screen gap-6">
      <ShowHeader
        logoUrl={company?.matrix_branch?.general_information.company_logo?.url}
        title={company?.matrix_branch?.general_information.fantasy_name}
        buttons={
          <div className="flex items-center gap-3">
            <Link to="/company/mine">
              <Button variant="outline-disabled">
                Ver perfil
              </Button>
            </Link>

            {dashboards.length > 0 && (
              <CreateDashboard
                outlineButton
                onCreateDashboard={onCreateDashboard}
              />
            )}
          </div>
        }
        className="mb-0"
      />

      <ErrorMessageWrapper
        className="flex-1"
        statusCode={dashboardsError?.status}
        refetchFunction={refetchDashboards}
      >
        <div
          ref={tabContainerRef}
          className="flex-1 flex flex-col overflow-hidden"
        >
          {!emptyDashboards && (
            <Tabs
              currentTab={currentTab}
              setCurrentTab={handleSetCurrentTab}
              isLoading={dashboardsIsPending}
              noRouting
              {...tabsDefaultWithFlexGrowClassName}
              tabsClassName={cn(
                tabsDefaultWithFlexGrowClassName.tabsClassName,
                "overflow-hidden",
              )}
              tabsContentClassName={cn(
                tabsDefaultWithFlexGrowClassName.tabsContentClassName,
                "mt-0 pt-4 overflow-hidden focus-visible:ring-0 focus-visible:ring-offset-0",
              )}
              tabsListClassName={cn(
                tabsDefaultWithFlexGrowClassName.tabsListClassName,
                "overflow-x-auto overflow-y-hidden thin-scroll focus-visible:ring-0 focus-visible:ring-offset-0 justify-normal",
              )}
              tabsTriggerClassName={cn(
                tabsDefaultWithFlexGrowClassName.tabsTriggerClassName,
                "focus-visible:ring-0 focus-visible:ring-offset-0",
              )}
              tabsList={filteredDashboards.map((dashboard, index) => ({
                value: dashboard.id,
                label: dashboard.display_name,
                body: (
                  <Dashboard
                    dashboardListItem={dashboard}
                    initialWidth={initialWidth}
                    isCreator={currentUserId === dashboard.creator?.id}
                    hasPermissionToUpdateDashboard={hasPermissionToUpdateDashboard}
                    onDeleteDashboard={() => onDeleteDashboard(index)}
                  />
                ),
              }))}
              BeforeTabsComponent={
                <div className="mr-2 inline-flex">
                  <div
                    className={cn(
                      'flex items-center bg-neutral-op-8 transition-all px-2 h-8 rounded-full',
                      !isSearchEnabled && 'cursor-pointer hover:bg-neutral-op-8/15'
                    )}
                    onClick={() => !isSearchEnabled && setIsSearchEnabled(true)}
                  >
                    <Button
                      variant="link-white"
                      onClick={() => setIsSearchEnabled(true)}
                    >
                      <SearchIcon size={16} />
                    </Button>

                    <div className={cn(
                      'flex items-center justify-between overflow-hidden transition-all duration-300',
                      isSearchEnabled ? 'w-40 opacity-100 ml-2' : 'w-0 opacity-0 pointer-events-none'
                    )}>
                      <input
                        ref={searchInputRef}
                        className="bg-transparent ring-0 outline-none w-full text-sm"
                        placeholder="Pesquisar"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />

                      <Button
                        variant="link-white"
                        className="text-white-300"
                        onClick={() => {
                          setSearch('');
                          setIsSearchEnabled(false);
                        }}
                      >
                        <XIcon size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              }
              EmptyTabsComponent={(
                <EmptyDataMessage className="flex-1" />
              )}
            />
          )}

          {emptyDashboards && (
            <EmptyDashboards
              onCreateDashboard={onCreateDashboard}
            />
          )}
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default Show
